import React from "react";
import { IDialogPromptSentence } from "../api/DialogPromptApi";
import { SentenceApi } from "../api/SentenceApi";
import { handleErrorMessage } from "../component/ErrorDialog";

interface UseConversationPinyinProps {
    history: any;
    location: any;
    setErrorMessage: (msg: string) => void;
}

export function useConversationPinyin({ history, location, setErrorMessage }: UseConversationPinyinProps) {

    const [conversationText, setConversationText] = React.useState("");
    const [conversationPinyin, setConversationPinyin] = React.useState("");
    const [conversationPinyinList, setConversationPinyinList] = React.useState<string[]>([]);
    const [conversationErrorText, setConversationErrorText] = React.useState<string | null>(null);

    const clearConversationErrors = React.useCallback(() => {
        setConversationErrorText(null);
    }, []);

    const clearConversationFields = React.useCallback(() => {
        setConversationText("");
        setConversationPinyin("");
        setConversationPinyinList([]);
        setConversationErrorText(null);
    }, []);

    const validatePinyin = React.useCallback(async (conversationText: string, updatedPinyin: string) => {
        const lines = conversationText.split("\n");
        const pinyinLines = updatedPinyin.split("\n");
        try {
            const validationResults = await Promise.all(
                lines.map((line, index) => {
                    const pinyinRaw = pinyinLines.length > index ? pinyinLines[index] : "";
                    const pinyinList = pinyinRaw.trim().split(/\s+/).filter(Boolean);
                    return SentenceApi.isTextPinyinValid(line, pinyinList)
                        .then(isValid => ({ isValid: isValid.data, line, pinyinList }))
                        .catch(err => {
                            console.error("Validation error:", err);
                            return { isValid: false, line, pinyinList };
                        });
                })
            );

            const firstInvalid = validationResults.find(result => !result.isValid);
            if (firstInvalid) {
                setConversationErrorText(`Invalid pinyin for: "${firstInvalid.line}" → "${firstInvalid.pinyinList.join(" ")}"`);
            } else {
                clearConversationErrors();
            }
        } catch (err) {
            handleErrorMessage(err, setErrorMessage, history, location);
            setConversationErrorText("Unable to validate pinyin");
        }

    }, [clearConversationErrors, setErrorMessage, history, location]);

    const setConversationFields = React.useCallback((dialog_prompt_sentence_list: IDialogPromptSentence[]) => {
        const conversationTextList = dialog_prompt_sentence_list.map(item => item.sentence_text || "");
        const conversationText = conversationTextList.join("\n") // Convert array to newline-separated string
        setConversationText(conversationText);

        setConversationPinyin("");
        setConversationPinyinList([]);
        setConversationErrorText(null);

        const loadNumericPinyin = async () => {
            try {
                const pinyinList = await Promise.all(
                    conversationTextList.map(async (line) => {
                        if (!line.trim()) return "";
                        const response = await SentenceApi.getTextPinyin(line);
                        return response.data.flat().join(" ");
                    })
                );

                setConversationPinyin(pinyinList.join("\n"));
                setConversationPinyinList(pinyinList);
                validatePinyin(conversationText, pinyinList.join("\n"));
            } catch (err) {
                handleErrorMessage(err, setErrorMessage, history, location);
                setConversationErrorText("Unable to fetch pinyin");
            }
        };
        loadNumericPinyin();

    }, [validatePinyin, setErrorMessage, history, location]);

    const handleConversationTextChange = React.useCallback(async (
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const updatedText = e.target.value;
        setConversationText(updatedText);
        const lines = updatedText.split("\n").map(line => line.trim()).filter(Boolean);

        try {
            const pinyinList = await Promise.all(
                lines.map(async line => {
                    const response = await SentenceApi.getTextPinyin(line);
                    return response.data.flat().join(" ");
                })
            );

            setConversationPinyin(pinyinList.join("\n"));
            setConversationPinyinList(pinyinList)
            setConversationErrorText(null);
        } catch (err) {
            handleErrorMessage(err, setErrorMessage, history, location);
            setConversationErrorText("Unable to fetch pinyin");
        }
    }, [setErrorMessage, history, location]);

    const handleConversationPinyinChange = React.useCallback(async (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const updatedPinyin = e.target.value;
        setConversationPinyin(updatedPinyin);
        const updatedPinyinList = updatedPinyin.split("\n").map(line => line.trim()).filter(Boolean);
        setConversationPinyinList(updatedPinyinList);
        validatePinyin(conversationText, updatedPinyin)
    }, [validatePinyin, conversationText]);

    return {
        conversationText,
        conversationPinyin,
        conversationPinyinList,
        conversationErrorText,
        setConversationFields,
        clearConversationFields,
        clearConversationErrors,
        handleConversationTextChange,
        handleConversationPinyinChange,
    };
}
